import React, { useEffect, useState } from "react";
import { Alert, CircularProgress, FormControlLabel, Grid, MenuItem, Paper, Stack, Switch, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import Button from "../../components/Common/Button";
import { setSnackBar } from "../../store/common/commonSlice";
import { useGetShopSettingsQuery, useUpdateShopSettingsMutation } from "../../store/shopAdmin/shopAdminApis";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const ShopSettingsView = () => {
    const dispatch = useDispatch();
    const { data, isFetching, error } = useGetShopSettingsQuery();
    const [save, { isLoading }] = useUpdateShopSettingsMutation();
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (data) setForm({ ...data });
    }, [data]);

    if (isFetching || !form) return <CircularProgress />;
    if (error) return <Alert severity="error">{error?.data?.message || "Failed to load settings"}</Alert>;

    const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const handleSave = async () => {
        try {
            await save({
                shipping_mode: form.shipping_mode,
                shipping_fee: form.shipping_mode === "free" ? 0 : num(form.shipping_fee),
                free_shipping_above: form.shipping_mode === "free_above" ? num(form.free_shipping_above) : null,
                first_order_offer_enabled: Boolean(form.first_order_offer_enabled),
                first_order_discount_type: form.first_order_discount_type,
                first_order_discount_value: num(form.first_order_discount_value),
                first_order_min_amount: num(form.first_order_min_amount) ?? 0,
            }).unwrap();
            dispatch(setSnackBar({ open: true, message: "Shop settings saved. New checkouts use them now.", severity: "success" }));
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not save settings", severity: "error" }));
        }
    };

    const offerText =
        form.first_order_discount_type === "percent"
            ? `${num(form.first_order_discount_value) || 0}% off`
            : `₹${num(form.first_order_discount_value) || 0} off`;

    return (
        <Stack spacing={2} sx={{ maxWidth: 820 }}>
            <Typography variant="h6">Shop Settings</Typography>
            <Alert severity="info">
                These apply to new checkouts immediately, with no app update. Orders already placed keep the prices and
                offers they were placed with.
            </Alert>

            <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Delivery charge
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField select fullWidth label="Rule" value={form.shipping_mode} onChange={set("shipping_mode")}>
                            <MenuItem value="free">Always free</MenuItem>
                            <MenuItem value="flat">Flat charge on every order</MenuItem>
                            <MenuItem value="free_above">Free above an order amount</MenuItem>
                        </TextField>
                    </Grid>
                    {form.shipping_mode !== "free" ? (
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth type="number" label="Delivery charge ₹" value={form.shipping_fee ?? ""} onChange={set("shipping_fee")} />
                        </Grid>
                    ) : null}
                    {form.shipping_mode === "free_above" ? (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Free delivery above ₹"
                                value={form.free_shipping_above ?? ""}
                                onChange={set("free_shipping_above")}
                                helperText="Checked after discounts"
                            />
                        </Grid>
                    ) : null}
                </Grid>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>
                        First-order offer for referred customers
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={Boolean(form.first_order_offer_enabled)} onChange={set("first_order_offer_enabled")} />}
                        label={form.first_order_offer_enabled ? "On" : "Off"}
                    />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    A customer who joined with a referral code gets this on their first paid order, once per mobile number.
                    It does not stack with coupons; the bigger discount applies.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label="Discount type"
                            value={form.first_order_discount_type}
                            onChange={set("first_order_discount_type")}
                            disabled={!form.first_order_offer_enabled}
                        >
                            <MenuItem value="percent">Percent (%)</MenuItem>
                            <MenuItem value="flat">Flat amount (₹)</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label={form.first_order_discount_type === "percent" ? "Discount %" : "Discount ₹"}
                            value={form.first_order_discount_value ?? ""}
                            onChange={set("first_order_discount_value")}
                            disabled={!form.first_order_offer_enabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Minimum order ₹"
                            value={form.first_order_min_amount ?? ""}
                            onChange={set("first_order_min_amount")}
                            disabled={!form.first_order_offer_enabled}
                        />
                    </Grid>
                </Grid>
                {form.first_order_offer_enabled ? (
                    <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                        Customers see: {offerText} on their first order of ₹{num(form.first_order_min_amount) || 0} or more.
                    </Typography>
                ) : null}
            </Paper>

            <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={handleSave} loading={isLoading} sx={{ minWidth: 160 }}>
                    Save settings
                </Button>
            </Stack>
        </Stack>
    );
};

export default ShopSettingsView;
